import { useNavigationBlock } from '@shared/hooks/use-navigation-block/useNavigationBlock';
import { sanitizeInput } from '@shared/utils/sanitize-input/sanitizeInput';
import * as React from 'react';

import type {
  UseChangeRequestFormParams,
  UseChangeRequestFormReturn,
} from './useChangeRequestForm.types';

import { changeRequestFormSchema } from '../../schemas/changeRequestFormSchema';

export function useChangeRequestForm({
  isError,
  isSuccess,
  reset,
  createRequest,
  onClose,
}: UseChangeRequestFormParams): UseChangeRequestFormReturn {
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState<
    Record<string, string>
  >({});
  const [showDiscardModal, setShowDiscardModal] = React.useState(false);
  const isDiscardingRef = React.useRef(false);

  const isDirty = !isSuccess && (title !== '' || description !== '');
  // Store isDirty in a ref so the navigation-block callback (below) always
  // reads the latest value at navigation time rather than at render time.
  const isDirtyRef = React.useRef(isDirty);
  isDirtyRef.current = isDirty;

  // Pass a function so that useNavigationBlock evaluates both refs
  // synchronously when a navigation event fires — not from the last render.
  // This prevents window.confirm firing after the user has already clicked
  // "Discard" in the UnsavedChangesModal (isDiscardingRef is set before navigate()).
  useNavigationBlock(
    React.useCallback(
      (): boolean => isDirtyRef.current && !isDiscardingRef.current,
      []
    )
  );

  const handleCloseAttempt = React.useCallback((): void => {
    if (isDirty) {
      setShowDiscardModal(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleDiscard = React.useCallback((): void => {
    isDiscardingRef.current = true;
    setShowDiscardModal(false);
    onClose();
  }, [onClose]);

  const handleCancelDiscard = React.useCallback((): void => {
    setShowDiscardModal(false);
  }, []);

  const handleSend = React.useCallback((): void => {
    const safeTitle = sanitizeInput(title);
    const safeDescription = sanitizeInput(description);

    const parsed = changeRequestFormSchema.safeParse({
      title: safeTitle ?? '',
      description: safeDescription ?? '',
    });

    if (!parsed.success) {
      const map: Record<string, string> = {};
      parsed.error.issues.forEach(e => {
        const key = e.path.join('.') || 'form';
        map[key] = e.message;
      });
      setValidationErrors(map);
      return;
    }

    setValidationErrors({});
    void createRequest(parsed.data.title, parsed.data.description);
  }, [createRequest, title, description]);

  const handleTitleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setTitle(e.target.value);
      setValidationErrors(prev => {
        if (!prev.title) return prev;
        const next = { ...prev };
        delete next.title;
        return next;
      });
      if (isError) reset();
    },
    [isError, reset]
  );

  const handleDescriptionChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
      setDescription(e.target.value);
      setValidationErrors(prev => {
        if (!prev.description) return prev;
        const next = { ...prev };
        delete next.description;
        return next;
      });
      if (isError) reset();
    },
    [isError, reset]
  );

  const handleCreateAnother = React.useCallback((): void => {
    reset();
    setTitle('');
    setDescription('');
    setValidationErrors({});
  }, [reset]);

  return {
    title,
    description,
    validationErrors,
    showDiscardModal,
    isDirty,
    handleTitleChange,
    handleDescriptionChange,
    handleSend,
    handleCloseAttempt,
    handleDiscard,
    handleCancelDiscard,
    handleCreateAnother,
  };
}
